import {
    StyledAutoAwesome,
    StyledField,
} from '@/components/decisionSupport/DecisionSupport.styled';
import { Utils } from '@/types/utils';
import { Typography } from '@mui/material';
import { type ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

type Recommendation = {
    recommendationRank: number;
};

export const useAxleRecommended = <T extends Recommendation>(recommendations?: T[] | null) => {
    return useMemo(
        () =>
            recommendations
                ? recommendations
                      .slice()
                      .sort((a, b) => a.recommendationRank - b.recommendationRank)
                      .at(0) ?? null
                : null,
        [recommendations],
    );
};

export const getRange = <T extends object>(
    list: T[],
    key: Utils.NumberKeys<T>,
): [min: number, max: number] => {
    const items = list.map((item) => item[key] as number);
    return [Math.min(...items), Math.max(...items)];
};

type BuildRecommendedColumnArgs<T> = {
    id: string;
    header: string;
    getLabel: (row: T) => string;
    isRecommended: (row: T) => boolean;
};

export const buildRecommendedColumn = <T,>({
    id,
    header,
    getLabel,
    isRecommended,
}: BuildRecommendedColumnArgs<T>): ColumnDef<T> => ({
    id,
    header,
    cell: ({ row }) => (
        <StyledField>
            <Typography variant='body2'>{getLabel(row.original)}</Typography>
            {isRecommended(row.original) ? <StyledAutoAwesome color='primary' /> : null}
        </StyledField>
    ),
});

export type DecisionSupportSelection = {
    id: string;
    label: string;
};

export type RecommendationRow = {
    id: string;
    label: string;
    recommendationRank: number;
    factors: Partial<
        Record<
            string,
            { sortValue: number | null; displayValue: string; isSatisfied: boolean | null }
        >
    >;
};

export type NormalizedSlotResult = {
    recommended: RecommendationRow[];
    notRecommended: RecommendationRow[];
    recommendedColumns: ColumnDef<RecommendationRow>[];
    notRecommendedColumns: ColumnDef<RecommendationRow>[];
};

export const EMPTY_RESULT: NormalizedSlotResult = {
    recommended: [],
    notRecommended: [],
    recommendedColumns: [],
    notRecommendedColumns: [],
};
